import { ROLES, PERMISSIONS } from '../constants/roles';
import axios from 'axios';
import config from '../config';

/**
 * ユーザーが特定の権限を持っているかチェック
 * @param {string} permission - チェックする権限
 * @param {string} userRole - ユーザーのロール
 * @return {boolean} - 権限があるかどうか
 */
export const hasPermission = (permission, userRole) => {
  if (!permission || !userRole || !PERMISSIONS[permission]) {
    return false;
  }
  
  return PERMISSIONS[permission].includes(userRole);
};

/**
 * バックエンドのロール名をフロントエンドのROLESにマッピングする関数
 * @param {string|object} backendRole - バックエンドから送られてくるロール名またはオブジェクト
 * @return {string} - フロントエンドのロール定数
 */
export const mapBackendRoleToFrontend = (backendRole) => {
  // 文字列変換を確実に行う
  const role = String(backendRole).toLowerCase();
  
  switch (role) {
    case 'admin':
      return ROLES.ADMIN;
    case 'corporate':
      return ROLES.CORPORATE;
    case 'poster':
      return ROLES.POSTER;
    case 'premium':
      return ROLES.PREMIUM;
    case 'viewer':
      return ROLES.VIEWER;
    default:
      return ROLES.VIEWER; // デフォルトは一般ユーザー
  }
};

/**
 * 指定されたロールの権限マップを計算する関数
 * @param {string} role - ユーザーロール
 * @return {Object} - 計算された権限マップ
 */
export const calculatePermissions = (role) => {
  const permissionMap = {};
  
  // すべての権限をチェックして権限マップを生成
  Object.keys(PERMISSIONS).forEach(permission => {
    permissionMap[permission] = PERMISSIONS[permission].includes(role);
  });
  
  return permissionMap;
};

/**
 * 現在のユーザー情報とトークンをローカルストレージから取得
 * @return {Object} - ユーザー情報とトークン
 */
export const getCurrentUser = () => {
  const token = localStorage.getItem('authToken');
  const userStr = localStorage.getItem('currentUser');
  
  // トークンの形式チェック (header.payload.signature)
  if (token && (!token.includes('.') || token.split('.').length !== 3)) {
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
    return { user: null, token: null };
  }
  
  let user = null;
  try {
    user = userStr ? JSON.parse(userStr) : null;
  } catch (err) {
    // エラーが発生した場合、ローカルストレージをクリア
    localStorage.removeItem('currentUser');
    localStorage.removeItem('authToken');
  }
  
  return { user, token };
};

/**
 * トークンをデコードしてペイロードを取得（JWTの場合）
 * @param {string} token - JWTトークン
 * @return {Object|null} - デコードされたペイロード、またはnull
 */
export const decodeToken = (token) => {
  if (!token) return null;
  
  try {
    // JWT形式: header.payload.signature
    const base64Payload = token.split('.')[1];
    const payload = JSON.parse(atob(base64Payload));
    return payload;
  } catch (err) {
    return null;
  }
};

/**
 * ユーザーがログインしているかチェック
 * @return {boolean} - ログイン状態
 */
export const isLoggedIn = () => {
  const token = localStorage.getItem('authToken');
  return !!token;
};

/**
 * ユーザーがゲスト（未ログイン）かどうかチェック
 * @return {boolean} - ゲスト状態
 */
export const isGuest = () => {
  return !isLoggedIn();
};

/**
 * トークンを検証してユーザー情報を取得する
 * @param {string} token - 検証するトークン
 * @return {Promise<Object>} - 検証結果とユーザー情報
 */
export const verifyTokenAndGetUser = async (token) => {
  if (!token) {
    return { valid: false, user: null };
  }
  
  try {
    const response = await axios.get(`${config.API_BASE_URL}/api/auth/verify`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    // レスポンスの形式チェック
    const data = response.data;
    if (data && data.valid === true && data.user) {
      return { valid: true, user: data.user };
    } else if (data && data.user) {
      // バックエンドがvalidフィールドを返さない場合、ユーザー情報があればvalidとみなす
      return { valid: true, user: data.user };
    } else {
      return { valid: false, user: null };
    }
  } catch (error) {
    return { valid: false, user: null };
  }
};

/**
 * 認証情報を保存する
 * @param {Object} userData - ユーザーデータ
 * @param {string} accessToken - アクセストークン
 * @param {string|null} refreshToken - リフレッシュトークン（オプション）
 */
export const saveAuthData = (userData, accessToken, refreshToken = null) => {
  localStorage.setItem('authToken', accessToken);
  
  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken);
  }
  
  localStorage.setItem('currentUser', JSON.stringify(userData));
};

/**
 * 認証情報をクリアする
 */
export const clearAuthData = () => {
  localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
  localStorage.removeItem('currentUser');
};

/**
 * トークンをリフレッシュする
 * @return {Promise<string|null>} - 新しいトークン or null
 */
export const refreshAuthToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('リフレッシュトークンがありません');
    }

    const response = await axios.post(`${config.API_BASE_URL}/api/auth/refresh`, {}, {
      headers: {
        'Authorization': `Bearer ${refreshToken}`
      }
    });

    const newToken = response.data.access_token;
    localStorage.setItem('authToken', newToken);
    
    return newToken;
  } catch (error) {
    return null;
  }
}; 